import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Loader2, Phone, Mail, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

// Facebook SDK App ID
const FB_APP_ID = process.env.REACT_APP_FB_APP_ID || '';

export function LoginPage() {
  const navigate = useNavigate();
  const { login, facebookAuth, sendOTP, verifyOTP, isAuthenticated } = useAuth();
  
  const [loginMethod, setLoginMethod] = useState('phone'); // 'phone', 'email', 'otp-verify'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  
  const otpRefs = useRef([]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Initialize Facebook SDK
  useEffect(() => {
    // Load Facebook SDK
    window.fbAsyncInit = function() {
      window.FB.init({
        appId: FB_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v19.0'
      });
    };

    // Load SDK script if not already loaded
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  }, []);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await sendOTP(phone);
      setLoginMethod('otp-verify');
      setResendTimer(60);
    } catch (err) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (otpOverride) => {
    const otpCode = (otpOverride || otp).join('');
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      await verifyOTP(phone, otpCode);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Invalid OTP');
      setOtp(['', '', '', '', '', '']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    
    setError('');
    setIsLoading(true);

    try {
      await sendOTP(phone);
      setResendTimer(60);
      setOtp(['', '', '', '', '', '']);
    } catch (err) {
      setError(err.message || 'Failed to resend OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto-submit when complete — pass newOtp directly to avoid stale closure
    if (newOtp.every(d => d !== '') && newOtp.join('').length === 6) {
      setTimeout(() => handleVerifyOTP(newOtp), 100);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length > 0) {
      const newOtp = [...otp];
      pastedData.split('').forEach((digit, idx) => {
        if (idx < 6) newOtp[idx] = digit;
      });
      setOtp(newOtp);
      
      if (pastedData.length === 6) {
        setTimeout(() => handleVerifyOTP(pastedData.split('')), 100);
      }
    }
  };

  const handleFacebookLogin = () => {
    setError('');
    setIsLoading(true);

    if (window.FB) {
      window.FB.login((response) => {
        if (response.authResponse) {
          const authResponse = response.authResponse;
          // Get user profile
          window.FB.api('/me', { fields: 'id,name,email,picture.type(large)' }, (profile) => {
            facebookAuth(
              authResponse.accessToken,
              authResponse.userID,
              {
                name: profile.name,
                email: profile.email,
                picture: profile.picture?.data?.url
              }
            ).then(() => {
              navigate('/dashboard');
            }).catch((err) => {
              setError(err.message || 'Facebook login failed');
              setIsLoading(false);
            });
          });
        } else {
          setError('Facebook login cancelled');
          setIsLoading(false);
        }
      }, { scope: 'email,public_profile' });
    } else {
      setError('Facebook SDK not loaded. Please refresh and try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="login-page">
      <Header />
      
      <div className="flex-1 bg-gradient-to-b from-[#E8F9EE] to-white py-12 sm:py-20 px-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">Welcome back</h1>
            <p className="text-gray-600 text-sm sm:text-base">Sign in to your GiriChat account</p>
          </div>

          <Card className="p-6 sm:p-8 border border-gray-200 shadow-lg bg-white">
            {/* Facebook Login */}
            <Button
              type="button"
              onClick={handleFacebookLogin}
              disabled={isLoading}
              className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white gap-3 py-6 mb-4"
              data-testid="login-facebook-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Continue with Facebook
            </Button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            {/* Login Method Toggle */}
            {loginMethod !== 'otp-verify' && (
              <div className="flex gap-2 mb-6">
                <Button
                  type="button"
                  variant={loginMethod === 'phone' ? 'default' : 'outline'}
                  className={`flex-1 ${loginMethod === 'phone' ? 'bg-[#00D95F] hover:bg-[#00C054] text-white' : ''}`}
                  onClick={() => { setLoginMethod('phone'); setError(''); }}
                  data-testid="login-method-phone"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  WhatsApp OTP
                </Button>
                <Button
                  type="button"
                  variant={loginMethod === 'email' ? 'default' : 'outline'}
                  className={`flex-1 ${loginMethod === 'email' ? 'bg-[#00D95F] hover:bg-[#00C054] text-white' : ''}`}
                  onClick={() => { setLoginMethod('email'); setError(''); }}
                  data-testid="login-method-email"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Email Login Form */}
            {loginMethod === 'email' && (
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-900">Email address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-900">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full bg-[#00D95F] hover:bg-[#00C054] text-white"
                  data-testid="login-submit-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign in'}
                </Button>
              </form>
            )}

            {/* Phone/OTP Login Form */}
            {loginMethod === 'phone' && (
              <form onSubmit={handleSendOTP} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-900">WhatsApp Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder="+91 9876543210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    data-testid="login-phone-input"
                  />
                  <p className="text-xs text-gray-500">We'll send a 6-digit OTP via WhatsApp</p>
                </div>

                <Button 
                  type="submit" 
                  disabled={isLoading || !phone}
                  className="w-full bg-[#00D95F] hover:bg-[#00C054] text-white"
                  data-testid="login-send-otp-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send OTP'}
                </Button>
              </form>
            )}

            {/* OTP Verification */}
            {loginMethod === 'otp-verify' && (
              <div className="space-y-4">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-sm text-gray-600 p-0 h-auto"
                  onClick={() => { setLoginMethod('phone'); setOtp(['', '', '', '', '', '']); setError(''); }}
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Change number
                </Button>

                <div className="text-center">
                  <p className="text-gray-600 mb-2">Enter the 6-digit code sent via WhatsApp to</p>
                  <p className="font-semibold text-gray-900">{phone}</p>
                </div>

                <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
                  {otp.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => otpRefs.current[index] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className="w-12 h-12 text-center text-xl font-bold"
                      data-testid={`otp-input-${index}`}
                    />
                  ))}
                </div>

                <Button 
                  type="button"
                  onClick={handleVerifyOTP}
                  disabled={isLoading || otp.some(d => d === '')}
                  className="w-full bg-[#00D95F] hover:bg-[#00C054] text-white"
                  data-testid="login-verify-otp-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify OTP'}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendTimer > 0 || isLoading}
                    className={`text-sm ${resendTimer > 0 ? 'text-gray-400' : 'text-[#00D95F] hover:underline'}`}
                  >
                    {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                  </button>
                </div>
              </div>
            )}

            <div className="mt-6 text-center text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/signup" className="text-[#00D95F] hover:underline font-semibold">
                Sign up
              </Link>
            </div>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
}
